import ufeApi from 'services/api/ufeApi';
import languageLocale from 'utils/LanguageLocale';
import addressUtils from 'utils/Address';
import userUtils from 'utils/User';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import Empty from 'constants/empty';
import { CHANNELS } from 'constants/Channels';

const { getCurrentCountry, getCurrentLanguage, getCurrentLanguageLocale } = languageLocale;

const SAMPLES_API_URLS = {
    STANDARD: '/api/util/samples',
    PRS_TEMPLATE: '/api/v3/util/samples',
    SMART_SAMPLES_TEMPLATE: '/gway/productaggregation/smartSamples?smartSamples=true'
};

const HTTP_STATUS_BAD_REQUEST = 400;

function buildStandardSamplesUrl() {
    const country = getCurrentCountry();
    const language = getCurrentLanguage();

    if (Sephora.configurationSettings.isPRSServiceEnabled) {
        return `${SAMPLES_API_URLS.PRS_TEMPLATE}?ch=rwd&loc=${language}-${country}`;
    }

    return SAMPLES_API_URLS.STANDARD;
}

function buildSmartSamplesConfig(user = Empty.Object, country) {
    const config = {
        url: `${SAMPLES_API_URLS.SMART_SAMPLES_TEMPLATE}&ch=${CHANNELS.RWD}&loc=${getCurrentLanguageLocale()}`
    };

    // Early return for anonymous users. No headers needed
    if (userUtils.isAnonymous()) {
        return config;
    }

    // For logged-in users, calculate and validate defaultSA headers
    const storage = Storage.session.getItem(LOCAL_STORAGE.PREFERRED_ZIP_CODE) || Empty.Object;
    const { preferredZipCode = Empty.String } = storage;

    const defaultZipCode = addressUtils.formatZipCode(preferredZipCode) || Empty.String;
    const defaultSAZipCode = user.defaultSAZipCode || addressUtils.getPreferredZipCodeOrDefault(defaultZipCode, { country });
    const defaultSACountryCode = user.defaultSACountryCode || country?.toLowerCase();

    if (!defaultSACountryCode || !defaultSAZipCode) {
        throw new Error('Smart samples requires country code and zip code');
    }

    config.headers = {
        defaultSACountryCode,
        defaultSAZipCode
    };

    return config;
}

/**
 * Fetches product samples based on configuration and user preferences
 * Supports both standard samples and smart samples (location-based)
 * @param {Object} options - Configuration options
 * @param {Object} options.user - User object with defaultSAZipCode and defaultSACountryCode
 * @returns {Promise<Object>} Samples data from API
 * @throws {Error} When API returns error or network failure
 */
function getSamples(options = Empty.Object) {
    const { isSampleSellThroughSmartSamples = Empty.Object } = Sephora.configurationSettings;
    let headers = {};
    let url = buildStandardSamplesUrl();

    if (isSampleSellThroughSmartSamples?.isEnabled) {
        const { user = Empty.Object } = options;

        try {
            const smartConfig = buildSmartSamplesConfig(user, getCurrentCountry());
            url = smartConfig.url;
            headers = smartConfig.headers;
        } catch (smartSamplesError) {
            Sephora.logger.error('Smart samples configuration error, falling back to standard samples', {
                error: smartSamplesError.message
            });
        }
    }

    return ufeApi
        .makeRequest(url, { method: 'GET', headers })
        .then(data => {
            if (data.errorCode || data.responseStatus >= HTTP_STATUS_BAD_REQUEST) {
                Sephora.logger.error('Get samples API error', {
                    errorCode: data.errorCode || data.responseStatus,
                    url
                });

                return Promise.reject(data);
            }

            return data;
        })
        .catch(error => {
            Sephora.logger.error('Get samples error', {
                error,
                url,
                message: error?.message || 'Unknown error'
            });

            return Promise.reject(error);
        });
}

export default getSamples;
